/**
 * AI创作中心 - 模型配置
 * 维护生图/生视频模型的映射关系、参数格式、分辨率选项等
 */

// ==================== 生图模型 ====================

export const imageModels = [
  {
    label: 'seedream5.0',
    value: 'M-0001-0001',
    desc: '指令响应更精准，生成效果更智能',
    sizes: ['2K', '3K', '4K'],
    ratios: ['16:9', '4:3', '1:1', '3:4', '9:16'],
    widthRange: [2560, 4096],
    heightRange: [1440, 4096],
  },
  {
    label: 'seedream4.5',
    value: 'M-0001-0002',
    desc: '画面全面优化，指令响应能力再次提升',
    sizes: ['2K', '4K'],
    ratios: ['16:9', '4:3', '1:1', '3:4', '9:16'],
    widthRange: [2560, 4096],
    heightRange: [1440, 4096],
  },
  {
    label: 'seedream4.0',
    value: 'M-0001-0003',
    desc: '提供基础的图像，整体和一致性保持',
    sizes: ['1K', '2K', '4K'],
    ratios: ['16:9', '4:3', '1:1', '3:4', '9:16'],
    widthRange: [1280, 4096],
    heightRange: [720, 4096],
  },
  {
    label: 'wan2.7-image-pro',
    value: 'M-0002-0001',
    desc: '全能参考，让角色演绎起来',
    sizes: ['1K', '2K', '4K'],
    ratios: ['16:9', '4:3', '1:1', '3:4', '9:16'],
    widthRange: [768, 4096],
    heightRange: [768, 4096],
  },
  {
    label: 'wan2.7-image',
    value: 'M-0002-0002',
    desc: '多镜头叙事，动态时长生成',
    sizes: ['1K', '2K'],
    ratios: ['16:9', '4:3', '1:1', '3:4', '9:16'],
    widthRange: [768, 2048],
    heightRange: [768, 2048],
  },
  {
    label: 'qwen-image-2.0-pro',
    value: 'M-0002-0004',
    desc: '强化一致性，风格与图文描述',
    sizes: ['1K', '2K'],
    ratios: ['16:9', '4:3', '1:1', '3:4', '9:16'],
    widthRange: [1024, 2048],
    heightRange: [1024, 2048],
  },
  {
    label: 'qwen-image-2.0',
    value: 'M-0002-0005',
    desc: '提供基础的图像，整体和一致性保持',
    sizes: ['1K', '2K'],
    ratios: ['16:9', '4:3', '1:1', '3:4', '9:16'],
    widthRange: [1024, 2048],
    heightRange: [1024, 2048],
  },
  {
    label: 'gpt-5.5',
    value: 'M-0003-0001',
    desc: '极致细节，真实自然的表达',
    sizes: [], // gpt-5.5 不支持 size，用 width/height
    ratios: ['16:9', '4:3', '1:1', '3:4', '9:16'],
    widthRange: null, // 乘积限制：655360 ~ 8294400
    heightRange: null,
    hasSetting: true, // 支持 setting 配置 (quality, lastTaskId)
    qualityOptions: ['low', 'medium', 'high', 'auto'], // gpt-5.5 特有的质量选项
  },
];

// 生图分辨率选项
export const imageSizeOptions = [
  { label: '1K', value: '1K' },
  { label: '2K', value: '2K' },
  { label: '3K', value: '3K' },
  { label: '4K', value: '4K' },
];

// 尺寸对应的基准像素（以短边为基准）
const sizeBasePixels = {
  '1K': 1024,
  '2K': 2048,
  '3K': 3072,
  '4K': 4096,
};

// 比例对应的宽高比数值
const ratioMap = {
  '16:9': 16 / 9,
  '4:3': 4 / 3,
  '1:1': 1,
  '3:4': 3 / 4,
  '9:16': 9 / 16,
};

/**
 * 根据尺寸和比例计算宽高
 * @param {string} size - 尺寸 (1K/2K/3K/4K)
 * @param {string} ratio - 比例 (16:9/4:3/1:1/3:4/9:16)
 * @returns {{ width: number, height: number }}
 */
export function sizeRatioToWidthHeight(size, ratio) {
  const base = sizeBasePixels[size] || 2048;
  const ratioValue = ratioMap[ratio] || 1;

  let width, height;
  if (ratioValue >= 1) {
    // 横屏或方形：短边 = base
    height = base;
    width = Math.round(base * ratioValue);
  } else {
    // 竖屏：短边 = base
    width = base;
    height = Math.round(base / ratioValue);
  }

  return { width, height };
}

// 生图数量选项
export const imageQuantityOptions = [
  { label: '1张', value: 1 },
  { label: '2张', value: 2 },
  { label: '3张', value: 3 },
  { label: '4张', value: 4 },
];

// ==================== 聊天对话模型 ====================

export const chatModels = [
  {
    label: 'GPT-5.5',
    value: 'M-0004-0001',
    desc: 'OpenAI 最新语言模型',
  },
  {
    label: 'doubao-seed-2.1-pro',
    value: 'M-0004-0002',
    desc: '豆包专业版语言模型',
  },
  {
    label: 'doubao-seed-2.1-turbo',
    value: 'M-0004-0003',
    desc: '豆包快速版语言模型',
  },
  {
    label: 'doubao-seed-2.0-pro',
    value: 'M-0004-0004',
    desc: '豆包2.0专业版语言模型',
  },
];

// ==================== 生视频模型 ====================

export const videoModels = [
  {
    label: 'doubao-seedance-2.0',
    value: 'M-0001-1001',
    desc: '极致细节，真实自然的表达',
    resolutions: ['480p', '720p', '1080p', '4k'],
    ratios: ['16:9', '4:3', '1:1', '3:4', '9:16', '21:9'],
    durationRange: [4, 15],
  },
  {
    label: 'doubao-seedance-2.0-fast',
    value: 'M-0001-1002',
    desc: '快速生成，高效表达',
    resolutions: ['480p', '720p'],
    ratios: ['16:9', '4:3', '1:1', '3:4', '9:16', '21:9'],
    durationRange: [4, 15],
  },
  {
    label: 'wan2.7文生视频',
    value: 'M-0002-1001',
    desc: '全能参考，让角色演绎起来',
    resolutions: ['720p', '1080p'],
    ratios: ['16:9', '4:3', '1:1', '3:4', '9:16'],
    durationRange: [2, 15],
  },
  {
    label: 'wan2.7图生视频',
    value: 'M-0002-1002',
    desc: '首尾帧控制，精准演绎',
    resolutions: ['720p', '1080p'],
    ratios: ['16:9', '4:3', '1:1', '3:4', '9:16'],
    durationRange: [2, 15],
    hasFrames: true, // 支持首尾帧
  },
  {
    label: 'wan2.7参考生视频',
    value: 'M-0002-1003',
    desc: '多参考素材，自由组合',
    resolutions: ['720p', '1080p'],
    ratios: ['16:9', '4:3', '1:1', '3:4', '9:16'],
    durationRange: [2, 10], // 有视频时 2-10 秒
  },
  {
    label: 'wan2.7视频编辑',
    value: 'M-0002-1004',
    desc: '视频编辑，智能修改',
    resolutions: ['720p', '1080p'],
    ratios: ['16:9', '4:3', '1:1', '3:4', '9:16'],
    durationRange: [2, 15],
  },
  {
    label: 'happyhorse文生视频',
    value: 'M-0002-1005',
    desc: '极致细节，真实自然的表达',
    resolutions: ['720p', '1080p'],
    ratios: ['16:9', '4:3', '1:1', '3:4', '9:16'],
    durationRange: [3, 15],
  },
  {
    label: 'happyhorse图生视频',
    value: 'M-0002-1006',
    desc: '首尾帧控制，精准演绎',
    resolutions: ['720p', '1080p'],
    ratios: ['16:9', '4:3', '1:1', '3:4', '9:16'],
    durationRange: [3, 15],
  },
  {
    label: 'happyhorse参考生视频',
    value: 'M-0002-1007',
    desc: '多参考素材，自由组合',
    resolutions: ['720p', '1080p'],
    ratios: ['16:9', '4:3', '1:1', '3:4', '9:16'],
    durationRange: [3, 15],
  },
  {
    label: 'happyhorse视频编辑',
    value: 'M-0002-1008',
    desc: '视频编辑，智能修改',
    resolutions: ['720p', '1080p'],
    ratios: ['16:9', '4:3', '1:1', '3:4', '9:16'],
    durationRange: [3, 15],
  },
];

// 视频分辨率选项
export const videoResolutionOptions = [
  { label: '480p', value: '480p' },
  { label: '720p', value: '720p' },
  { label: '1080p', value: '1080p' },
  { label: '4k', value: '4k' },
];

// ==================== 工具函数 ====================

/**
 * 构建生图模型的 promptSetting (JSON字符串)
 * @param {Object} params - 用户输入参数
 * @param {string} params.modelCode - 模型代码
 * @param {string} params.prompt - 提示词
 * @param {number} params.number - 数量
 * @param {string} params.size - 分辨率 (1K/2K/3K/4K)，与 width/height 互斥
 * @param {number} params.width - 宽度
 * @param {number} params.height - 高度
 * @param {string[]} params.images - 参考图URL数组
 * @param {Object} params.setting - 特殊配置 (gpt-5.5 需要)
 * @returns {string} JSON字符串
 */
export function buildImagePromptSetting(params) {
  const { modelCode, prompt, number, size, width, height, images, setting } =
    params;
  const payload = {
    modelCode,
    prompt,
    number,
  };

  // width/height 优先（由比例+尺寸计算得出），没有则用 size
  if (width && height) {
    payload.width = width;
    payload.height = height;
  } else if (size) {
    payload.size = size;
  }

  if (images && images.length > 0) {
    payload.images = images;
  }

  if (setting) {
    payload.setting = setting;
  }

  return JSON.stringify(payload);
}

/**
 * 构建聊天对话模型的 promptSetting (JSON字符串)
 * @param {Object} params - 用户输入参数
 * @param {string} params.modelCode - 模型代码
 * @param {string} params.prompt - 提示词
 * @returns {string} JSON字符串
 */
export function buildChatPromptSetting(params) {
  const { modelCode, prompt } = params;
  const payload = {
    modelCode,
    prompt,
  };
  return JSON.stringify(payload);
}

/**
 * 构建生视频模型的 promptSetting (JSON字符串)
 * @param {Object} params - 用户输入参数
 * @param {string} params.modelCode - 模型代码
 * @param {string} params.prompt - 提示词
 * @param {string} params.ratio - 比例
 * @param {string} params.duration - 时长(秒)
 * @param {string} params.resolution - 分辨率
 * @param {string} params.firstFrameImage - 首帧图片URL
 * @param {string} params.lastFrameImage - 尾帧图片URL
 * @param {string[]} params.referenceVideos - 参考视频URL数组
 * @param {string[]} params.referenceImages - 参考图片URL数组
 * @param {string[]} params.referenceAudios - 参考音频URL数组
 * @param {Object} params.setting - 特殊配置
 * @returns {string} JSON字符串
 */
export function buildVideoPromptSetting(params) {
  const {
    modelCode,
    prompt,
    ratio,
    duration,
    resolution,
    firstFrameImage,
    lastFrameImage,
    referenceVideos,
    referenceImages,
    referenceAudios,
    setting,
  } = params;

  const payload = {
    modelCode,
    prompt,
    ratio,
    duration,
    resolution,
  };

  if (firstFrameImage) {
    payload.firstFrameImage = firstFrameImage;
  }
  if (lastFrameImage) {
    payload.lastFrameImage = lastFrameImage;
  }
  if (referenceVideos && referenceVideos.length > 0) {
    payload.referenceVideos = referenceVideos;
  }
  if (referenceImages && referenceImages.length > 0) {
    payload.referenceImages = referenceImages;
  }
  if (referenceAudios && referenceAudios.length > 0) {
    payload.referenceAudios = referenceAudios;
  }
  if (setting) {
    payload.setting = setting;
  }

  return JSON.stringify(payload);
}

/**
 * 根据模型代码查找模型配置
 */
export function findImageModelByCode(code) {
  return imageModels.find((m) => m.value === code);
}

export function findVideoModelByCode(code) {
  return videoModels.find((m) => m.value === code);
}

export function findChatModelByCode(code) {
  return chatModels.find((m) => m.value === code);
}
